import * as React from "react";
import { CalendarOff, Check, MoreHorizontal, X } from "lucide-react";
import {
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DataTableEmpty,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  StatusPill,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import {
  LEAVE_TYPE_LABELS,
  formatDay,
  useDecideLeave,
  useLeaveRequests,
  type LeaveRequest,
  type LeaveStatus,
} from "@/lib/teamPay/api";
import { ErrorBanner, NativeSelect } from "@/components/team-pay/shared";
import { errorMessage, useTeamWords } from "@/lib/teamPay/words";

/** Phase SC-22d — leave requests from the team, pending first. */

const STATUS_PILL: Record<LeaveStatus, { status: StatusPillStatus; label: string }> = {
  pending: { status: "invited", label: "Waiting" },
  approved: { status: "active", label: "Approved" },
  rejected: { status: "suspended", label: "Rejected" },
  cancelled: { status: "archived", label: "Cancelled" },
};

function dateRange(r: LeaveRequest): string {
  if (r.start_date === r.end_date) return formatDay(r.start_date) + (r.half_day ? " (half)" : "");
  return `${formatDay(r.start_date)} – ${formatDay(r.end_date)}`;
}

export function LeaveTab({ canApprove }: { canApprove: boolean }) {
  const words = useTeamWords();
  const [status, setStatus] = React.useState<LeaveStatus | "all">("pending");
  const leaves = useLeaveRequests(status === "all" ? undefined : status);
  const decide = useDecideLeave();
  const items = leaves.data?.items ?? [];

  const run = async (r: LeaveRequest, approve: boolean) => {
    try {
      const res = await decide.mutateAsync({ id: r.id, approve });
      toast.success(
        approve
          ? `Approved · ${res.paid_days} paid, ${res.unpaid_days} unpaid.`
          : "Leave request rejected.",
      );
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't save the decision."));
    }
  };

  return (
    <div className="space-y-3">
      {leaves.isError ? (
        <ErrorBanner
          message={errorMessage(leaves.error, "Couldn't load leave requests.")}
          onRetry={() => leaves.refetch()}
        />
      ) : null}
      <DataTable responsive="cards">
        <DataTableToolbar>
          <NativeSelect
            aria-label="Status filter"
            className="w-40"
            value={status}
            onChange={(e) => setStatus(e.target.value as LeaveStatus | "all")}
          >
            <option value="pending">Waiting</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
            <option value="all">All</option>
          </NativeSelect>
          <DataTableToolbarSpacer />
        </DataTableToolbar>
        <DataTableScroll>
          <Table>
            <TableCaption className="sr-only">Leave requests</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{words.member}</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Days</TableHead>
                <TableHead>Status</TableHead>
                {canApprove ? (
                  <TableHead className="w-[1%] text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                ) : null}
              </TableRow>
            </TableHeader>
            {leaves.isLoading ? (
              <DataTableSkeleton rows={5} columns={canApprove ? 6 : 5} />
            ) : (
              <TableBody>
                {items.map((r) => (
                  <TableRow
                    key={r.id}
                    className="hover:bg-muted/40 transition-colors duration-[120ms]"
                  >
                    <TableCell>
                      <span className="block font-medium">{r.member_name ?? "—"}</span>
                      {r.reason ? (
                        <span className="text-muted-foreground line-clamp-1 block text-xs">
                          {r.reason}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{dateRange(r)}</TableCell>
                    <TableCell>{LEAVE_TYPE_LABELS[r.leave_type]}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.days_requested}
                      {r.status === "approved" && r.unpaid_days > 0 ? (
                        <span className="text-muted-foreground block text-xs">
                          {r.unpaid_days} unpaid
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        status={STATUS_PILL[r.status].status}
                        label={STATUS_PILL[r.status].label}
                      />
                    </TableCell>
                    {canApprove ? (
                      <TableCell className="text-right">
                        {r.status === "pending" ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8"
                                disabled={decide.isPending}
                                aria-label={`Decide leave for ${r.member_name ?? "member"}`}
                              >
                                <MoreHorizontal aria-hidden="true" className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-40">
                              <DropdownMenuItem onSelect={() => run(r, true)}>
                                <Check aria-hidden="true" className="size-4" />
                                Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => run(r, false)}>
                                <X aria-hidden="true" className="size-4" />
                                Reject
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>
        {!leaves.isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<CalendarOff aria-hidden="true" />}
            title={status === "pending" ? "No leave to approve" : "No leave requests"}
            description="Requests sent from the portal show here."
          />
        ) : null}
      </DataTable>
    </div>
  );
}
